import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDemo } from "../context/DemoContext";
import {
  listCustomers,
  listTransactions,
  listPlatformInternalAccounts,
  listCustomerInternalAccounts,
} from "../services/gridApi";
import { formatCurrencyAmount } from "../data/mock";

function formatAmount(amount: number, currency: string): string {
  if (currency === "BTC") return (amount / 100_000_000).toFixed(8) + " BTC";
  return "$" + (amount / 100).toLocaleString("en-US", { minimumFractionDigits: 2 });
}

export default function Home() {
  const { auth } = useAuth();
  const { demo, getDemoCustomers, getDemoInternalAccounts, getDemoTransactions } = useDemo();
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalTransactions: 0,
    platformBalances: [] as any[],
    internalAccountCount: 0,
  });
  const [recentTx, setRecentTx] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (demo.enabled) {
      const customers = getDemoCustomers();
      const platformIA = getDemoInternalAccounts("PLATFORM");
      const allIA = getDemoInternalAccounts();
      const txList = getDemoTransactions();
      setStats({
        totalCustomers: customers.length,
        totalTransactions: txList.length,
        platformBalances: platformIA,
        internalAccountCount: allIA.length - platformIA.length,
      });
      setRecentTx(txList.slice(0, 5));
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers({ limit: 1 }).catch(() => ({ data: [], totalCount: 0 })),
      listTransactions({ limit: 5, sortOrder: "desc" }).catch(() => ({ data: [], totalCount: 0 })),
      listPlatformInternalAccounts().catch(() => ({ data: [] })),
      listCustomerInternalAccounts({ limit: 100 }).catch(() => ({ data: [] })),
    ]).then(([cust, tx, platform, intAccts]) => {
      setStats({
        totalCustomers: (cust as any).totalCount || cust.data?.length || 0,
        totalTransactions: (tx as any).totalCount || tx.data?.length || 0,
        platformBalances: platform.data || [],
        internalAccountCount: intAccts.data?.length || 0,
      });
      setRecentTx(tx.data || []);
      setLoading(false);
    });
  }, [demo.enabled, getDemoCustomers, getDemoInternalAccounts, getDemoTransactions]);

  return (
    <div className="p-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#00B3E0]/20 via-[#00B3E0]/10 to-transparent border border-[#00B3E0]/10 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
              Welcome to Grid, {auth.partnerName}
            </h1>
            <p className="text-sm text-[#8A8A8A] mt-1">
              {demo.enabled ? "Demo mode - using mock data for demonstration" : "Live sandbox dashboard - all data from real Grid API calls"}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/use-cases" className="bg-[#00B3E0] text-black font-semibold px-4 py-2 rounded-lg hover:bg-[#00C4F5] text-sm">
              Explore Use Cases
            </Link>
            <Link to="/api-explorer" className="border border-[#333333] text-[#F8F8F7] px-4 py-2 rounded-lg hover:bg-[#222222] text-sm">
              API Explorer
            </Link>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#8A8A8A]">
          <div className="w-5 h-5 border-2 border-[#00B3E0] border-t-transparent rounded-full animate-spin" />
          Loading dashboard data from Grid API...
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
              <div className="text-xs text-[#8A8A8A] mb-1">Total Customers</div>
              <div className="text-2xl font-semibold text-[#F8F8F7]">{stats.totalCustomers}</div>
              <div className="text-xs text-[#666666] mt-1">from GET /customers</div>
            </div>
            <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
              <div className="text-xs text-[#8A8A8A] mb-1">Transactions</div>
              <div className="text-2xl font-semibold text-[#F8F8F7]">{stats.totalTransactions}</div>
              <div className="text-xs text-[#666666] mt-1">from GET /transactions</div>
            </div>
            <div className="bg-[#111111] border border-indigo-500/20 rounded-lg p-5">
              <div className="text-xs text-[#8A8A8A] mb-1">Platform Treasury</div>
              <div className="text-lg font-semibold text-[#F8F8F7]">
                {stats.platformBalances.length > 0
                  ? stats.platformBalances.map((a: any) => (
                      <div key={a.id} className="text-sm">
                        {a.balance ? formatCurrencyAmount(a.balance) : formatAmount(a.balance?.amount || 0, a.balance?.currency?.code || "USD")}
                      </div>
                    ))
                  : <span className="text-sm text-[#666666]">No platform accounts</span>
                }
              </div>
              <div className="text-xs text-[#666666] mt-1">shared platform accounts</div>
            </div>
            <div className="bg-[#111111] border border-emerald-500/20 rounded-lg p-5">
              <div className="text-xs text-[#8A8A8A] mb-1">Customer Wallets</div>
              <div className="text-2xl font-semibold text-[#F8F8F7]">{stats.internalAccountCount}</div>
              <div className="text-xs text-[#666666] mt-1">per-customer accounts</div>
            </div>
          </div>

          {/* Two Column: Recent Tx + Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Transactions */}
            <div className="lg:col-span-2 bg-[#111111] border border-[#222222] rounded-lg">
              <div className="flex items-center justify-between p-5 border-b border-[#222222]">
                <h2 className="text-sm font-semibold text-[#F8F8F7]">Recent Transactions (Live)</h2>
                <Link to="/transactions" className="text-xs text-[#00B3E0] hover:text-[#00C4F5] font-medium">
                  View all
                </Link>
              </div>
              {recentTx.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#666666]">
                  No transactions yet. Use the Simulator to create some!
                </div>
              ) : (
                <div className="divide-y divide-[#222222]">
                  {recentTx.map((tx: any) => (
                    <div key={tx.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-white/[0.02]">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          tx.type === "INCOMING" ? "bg-emerald-500/10 text-emerald-400" : "bg-[#00B3E0]/10 text-[#00B3E0]"
                        }`}>
                          {tx.type === "INCOMING" ? "IN" : "OUT"}
                        </div>
                        <div>
                          <div className="text-sm text-[#F8F8F7]">{tx.description || tx.id?.slice(0, 20)}</div>
                          <div className="text-xs text-[#666666]">
                            {tx.status} &middot; {new Date(tx.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className={`text-xs font-medium px-2 py-1 rounded ${
                        tx.status === "COMPLETED" ? "bg-emerald-500/10 text-emerald-400" :
                        tx.status === "PENDING" ? "bg-yellow-500/10 text-yellow-400" :
                        tx.status === "FAILED" ? "bg-red-500/10 text-red-400" :
                        "bg-[#222222] text-[#8A8A8A]"
                      }`}>
                        {tx.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
                <h3 className="text-sm font-semibold text-[#F8F8F7] mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {[
                    { label: "Create Customer", to: "/customers" },
                    { label: "New Transfer", to: "/transfer" },
                    { label: "View Corridors", to: "/corridors" },
                    { label: "Sandbox Simulator", to: "/simulator" },
                    { label: "API Credentials", to: "/api-credentials" },
                  ].map((action) => (
                    <Link
                      key={action.label}
                      to={action.to}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.04] transition-colors group"
                    >
                      <span className="text-sm text-[#CCCCCC] group-hover:text-[#F8F8F7]">{action.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
                <h3 className="text-sm font-semibold text-[#F8F8F7] mb-3">Environment</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">Status</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-emerald-400">Connected</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">Environment</span>
                    <span className="text-[#F8F8F7]">Sandbox</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#8A8A8A]">API Version</span>
                    <span className="text-[#CCCCCC] font-mono">2025-10-13</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
